import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { ShipmentPackagingType } from "app/pages/shipments/types/domain";
import { FormProvider, useForm } from "react-hook-form";
import { IncotermsEnum } from "types/Incoterms";
import { RoutingTypeEnum } from "types/RoutingType";
import { UnloadingDetailsEnum } from "types/UnloadingDetails";

import { useFinalizeAndSendQr } from "../_hooks/useFinalizeAndSendQr";
import { useQuoteRequestSelector } from "../_store";
import { QuoteStepIndex } from "../types";
import { ContainersStep } from "./_components/ContainersStep";
import PartnersStep from "./_components/PartnersStep";
import { QuotePreferencesStep } from "./_components/QuotePreferencesStep";
import ReviewStep from "./_components/ReviewStep";
import { RoutingStep } from "./_components/RoutingStep";
import ShipmentDetailsStep from "./_components/ShipmentDetailsStep";
import TeamStep from "./_components/TeamMembersStep";
import {
  ContainerSchema,
  containersSchema,
  FinalSchema,
  partnersSchema,
  quotePreferencesSchema,
  routingSchema,
  shipmentDetailsSchema,
  StopSchema,
  teamSchema,
} from "./_schemas";

const schemaMap = {
  [QuoteStepIndex.Routing]: routingSchema,
  [QuoteStepIndex.Containers]: containersSchema,
  [QuoteStepIndex.Service]: quotePreferencesSchema,
  [QuoteStepIndex.Shipment]: shipmentDetailsSchema,
  [QuoteStepIndex.Partners]: partnersSchema,
  [QuoteStepIndex.Team]: teamSchema,
  [QuoteStepIndex.Review]: teamSchema,
};

export const QuoteRequestFCLForm: React.FC = () => {
  const { quoteRequest: airQuoteRequest, stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const { finalizeAndSendQr, resetLocalState } = useFinalizeAndSendQr();

  // @ts-expect-error this is fine
  const currentSchema = schemaMap[stepIndex];

  const selectedRouting = airQuoteRequest.load_spec?.routing_type;
  const indexOffset = selectedRouting === RoutingTypeEnum.D2D || selectedRouting === RoutingTypeEnum.D2P ? 0 : 1;

  const methods = useForm<FinalSchema>({
    mode: "onSubmit",
    resolver: zodResolver(currentSchema),
    values: {
      incoterms: airQuoteRequest.load_spec?.incoterms as IncotermsEnum,
      routing_type: airQuoteRequest.load_spec?.routing_type as RoutingTypeEnum,
      originStop: (airQuoteRequest?.stops?.[0] as StopSchema) ?? {},
      originPort: (airQuoteRequest?.stops?.[1 - indexOffset] as StopSchema) ?? {},
      destinationPort: (airQuoteRequest?.stops?.[2 - indexOffset] as StopSchema) ?? {},
      destinationStop: (airQuoteRequest?.stops?.[3 - indexOffset] as StopSchema) ?? {},
      cargo_ready_date: airQuoteRequest.load_spec?.cargo_ready_date as string,
      target_delivery_date: airQuoteRequest.load_spec?.target_delivery_date,
      carrierRoutingPrefNotes: airQuoteRequest.carrier_routing_pref_notes,
      quoteTypes: airQuoteRequest.load_spec?.quote_types ?? [],
      directSailingRequired: airQuoteRequest.load_spec?.direct_sailing_required,
      capacityGuaranteeRequired: airQuoteRequest.load_spec?.capacity_guarantee_required,
      containers: (airQuoteRequest.load_spec?.containers as ContainerSchema[]) ?? [],
      referenceNumber: airQuoteRequest.reference_number ?? undefined,
      deadlineRespondAt: airQuoteRequest.deadline_respond_at as string,
      unloadingDetails: airQuoteRequest.unloading_details as UnloadingDetailsEnum,
      isPalletized: airQuoteRequest.load_spec?.is_palletized as boolean,
      palletCount: airQuoteRequest.load_spec?.pallet_count as number,
      packagingType: airQuoteRequest.load_spec?.packaging_type as ShipmentPackagingType,
      packingCount: airQuoteRequest.load_spec?.packing_count as number,
      isOverweight: airQuoteRequest.load_spec?.is_overweight,
      commodities: airQuoteRequest.load_spec?.commodities ?? "",
      isHazardous: airQuoteRequest.is_hazardous as boolean,
      insuranceRequired: airQuoteRequest.insurance_required as boolean,
      includeCustomsClearance: airQuoteRequest.load_spec?.include_customs_clearance,
      hazardousGoodsDetails: airQuoteRequest.hazardous_goods_details ?? undefined,
      goodsValue: airQuoteRequest.goods_value ?? undefined,
      additionalNotes: airQuoteRequest.note ?? undefined,
      partners: airQuoteRequest.partners ?? [],
      teammates: airQuoteRequest.teammates,
      // This cast is neccessary as typescript struggles with multiple discriminated unions
    } as FinalSchema,
  });

  const onSubmit = () => {
    finalizeAndSendQr();
  };

  if (resetLocalState) {
    return null;
  }

  return (
    <div>
      <FormProvider {...methods}>
        <RoutingStep onSubmit={onSubmit} />
        <QuotePreferencesStep onSubmit={onSubmit} />
        <ContainersStep onSubmit={onSubmit} />
        <ShipmentDetailsStep onSubmit={onSubmit} />
        <PartnersStep onSubmit={onSubmit} />
        <TeamStep onSubmit={onSubmit} />
        <ReviewStep onSubmit={onSubmit} />
      </FormProvider>
    </div>
  );
};
